import type { FileHashCache, FileHashEntry, SyncStatus } from '@pyranger/types';
import type { EditorContext } from '@pyranger/types';
import { fileHashCacheSchema } from '@pyranger/types';
import {
    FILE_HASH_CACHE_KEY,
    HASH_COMPUTATION_TIMEOUT_MS,
    MAX_FILE_SIZE_BYTES,
} from '@pyranger/constants';

import { MpRawMode } from './mpRawMode';
import {
    generateBatchFileHashScript,
    generateSingleFileHashScript,
    parseBatchHashResponse,
    parseSingleHashResponse,
} from './deviceScripts';

// ─── Browser-side hash computation ─────────────────────────────────────────────

/**
 * Compute a SHA-256 hash of the given content using the Web Crypto API.
 * Returns a 64-character lowercase hex string.
 *
 * @throws Error if content exceeds MAX_FILE_SIZE_BYTES (1MB)
 * @throws Error if crypto.subtle.digest fails
 */
export async function computeFileHash(content: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(content);

    if (data.byteLength > MAX_FILE_SIZE_BYTES) {
        throw new Error(
            `File exceeds maximum supported size of ${MAX_FILE_SIZE_BYTES} bytes`,
        );
    }

    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = new Uint8Array(hashBuffer);
    return Array.from(hashArray)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}

// ─── Device-side hash computation ──────────────────────────────────────────────

/**
 * Compute the SHA-256 hash of a single file on the device via raw REPL.
 * Returns the 64-char hex hash string, or null if the file is inaccessible or an error occurs.
 */
export async function computeDeviceFileHash(
    context: EditorContext,
    path: string,
): Promise<string | null> {
    const raw = await MpRawMode.begin(context);
    try {
        const output = await raw.exec(
            generateSingleFileHashScript(path),
            HASH_COMPUTATION_TIMEOUT_MS,
        );
        return parseSingleHashResponse(output);
    } catch (error) {
        console.warn(
            `[FileIntegrity] Device hash computation failed for ${path}`,
            error,
        );
        return null;
    } finally {
        await raw.end();
    }
}

/**
 * Compute SHA-256 hashes for multiple files on the device in a single raw REPL session.
 * Returns a map of path → hash (null for files that couldn't be hashed).
 */
export async function computeDeviceFileHashes(
    context: EditorContext,
    paths: string[],
): Promise<Record<string, string | null>> {
    if (paths.length === 0) return {};

    const raw = await MpRawMode.begin(context);
    try {
        const output = await raw.exec(
            generateBatchFileHashScript(paths),
            HASH_COMPUTATION_TIMEOUT_MS,
        );
        return parseBatchHashResponse(output);
    } catch (error) {
        console.warn(
            '[FileIntegrity] Batch device hash computation failed',
            error,
        );
        // Return null for all paths on total failure
        const result: Record<string, string | null> = {};
        for (const p of paths) {
            result[p] = null;
        }
        return result;
    } finally {
        await raw.end();
    }
}

// ─── Hash cache management ─────────────────────────────────────────────────────

/** Module-level in-memory cache, loaded lazily on first access */
let inMemoryCache: FileHashCache = {};
let cacheLoaded = false;

/** Debounce timer for persistence */
let persistDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const PERSIST_DEBOUNCE_MS = 1000;

/**
 * Ensure the in-memory cache is loaded from localStorage (lazy initialization).
 */
function ensureCacheLoaded(): void {
    if (!cacheLoaded) {
        inMemoryCache = loadHashCache();
        cacheLoaded = true;
    }
}

/**
 * Load the hash cache from localStorage. Validates with Zod schema.
 * Returns an empty cache if localStorage is unavailable, JSON is corrupt,
 * or schema validation fails.
 */
export function loadHashCache(): FileHashCache {
    try {
        if (typeof localStorage === 'undefined') {
            console.warn(
                '[FileIntegrity] localStorage unavailable, starting with empty cache',
            );
            return {};
        }

        const raw = localStorage.getItem(FILE_HASH_CACHE_KEY);
        if (!raw) {
            return {};
        }

        const parsed = JSON.parse(raw);
        const result = fileHashCacheSchema.safeParse(parsed);

        if (!result.success) {
            console.warn(
                '[FileIntegrity] Stored hash cache failed schema validation, discarding',
                result.error.issues,
            );
            return {};
        }

        // Convert from PersistedFileHashCache format (version + devices) to flat FileHashCache
        const cache: FileHashCache = {};
        for (const [deviceKey, files] of Object.entries(result.data.devices)) {
            cache[deviceKey] = {};
            for (const [filePath, entry] of Object.entries(files)) {
                cache[deviceKey][filePath] = {
                    hash: entry.hash,
                    timestamp: entry.timestamp,
                    path: filePath,
                };
            }
        }

        return cache;
    } catch (error) {
        console.warn(
            '[FileIntegrity] Failed to load hash cache, starting with empty cache',
            error,
        );
        return {};
    }
}

/**
 * Persist the full hash cache to localStorage.
 * Converts from flat FileHashCache to PersistedFileHashCache format.
 * If quota exceeded, evicts oldest entries until write succeeds.
 * If localStorage is unavailable, logs a warning.
 */
export function persistHashCache(cache: FileHashCache): void {
    try {
        if (typeof localStorage === 'undefined') {
            console.warn(
                '[FileIntegrity] localStorage unavailable, cannot persist cache',
            );
            return;
        }

        const persisted = convertToPersistedFormat(cache);
        tryWriteToLocalStorage(persisted);
    } catch (error) {
        console.warn('[FileIntegrity] Failed to persist hash cache', error);
    }
}

/**
 * Convert flat FileHashCache to the persisted format with version wrapper.
 */
function convertToPersistedFormat(cache: FileHashCache): {
    version: 1;
    devices: Record<
        string,
        Record<string, { hash: string; timestamp: string }>
    >;
} {
    const devices: Record<
        string,
        Record<string, { hash: string; timestamp: string }>
    > = {};

    for (const [deviceKey, files] of Object.entries(cache)) {
        devices[deviceKey] = {};
        for (const [filePath, entry] of Object.entries(files)) {
            devices[deviceKey][filePath] = {
                hash: entry.hash,
                timestamp: entry.timestamp,
            };
        }
    }

    return { version: 1, devices };
}

/**
 * Attempt to write the persisted cache to localStorage.
 * If quota is exceeded, evict oldest entries and retry.
 */
function tryWriteToLocalStorage(persisted: {
    version: 1;
    devices: Record<
        string,
        Record<string, { hash: string; timestamp: string }>
    >;
}): void {
    const json = JSON.stringify(persisted);

    try {
        localStorage.setItem(FILE_HASH_CACHE_KEY, json);
    } catch (error) {
        if (isQuotaExceededError(error)) {
            console.warn(
                '[FileIntegrity] localStorage quota exceeded, evicting oldest entries',
            );
            evictOldestAndRetry(persisted);
        } else {
            throw error;
        }
    }
}

/**
 * Check if an error is a quota exceeded error.
 */
function isQuotaExceededError(error: unknown): boolean {
    return (
        error instanceof DOMException &&
        (error.name === 'QuotaExceededError' ||
            error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
    );
}

/**
 * Evict the oldest entries from the persisted cache and retry writing.
 * Entries are ordered by timestamp; oldest entries are removed first.
 */
function evictOldestAndRetry(persisted: {
    version: 1;
    devices: Record<
        string,
        Record<string, { hash: string; timestamp: string }>
    >;
}): void {
    // Collect all entries with their location info
    const allEntries: Array<{
        deviceKey: string;
        filePath: string;
        timestamp: string;
    }> = [];

    for (const [deviceKey, files] of Object.entries(persisted.devices)) {
        for (const [filePath, entry] of Object.entries(files)) {
            allEntries.push({
                deviceKey,
                filePath,
                timestamp: entry.timestamp,
            });
        }
    }

    // Sort by timestamp ascending (oldest first)
    allEntries.sort((a, b) => a.timestamp.localeCompare(b.timestamp));

    // Remove entries one by one until write succeeds or cache is empty
    for (const entry of allEntries) {
        delete persisted.devices[entry.deviceKey][entry.filePath];

        // Clean up empty device records
        if (Object.keys(persisted.devices[entry.deviceKey]).length === 0) {
            delete persisted.devices[entry.deviceKey];
        }

        try {
            const json = JSON.stringify(persisted);
            localStorage.setItem(FILE_HASH_CACHE_KEY, json);
            return; // Write succeeded
        } catch (error) {
            if (!isQuotaExceededError(error)) {
                throw error;
            }
            // Continue evicting
        }
    }

    // Cache is now empty, try one last time
    try {
        localStorage.setItem(
            FILE_HASH_CACHE_KEY,
            JSON.stringify({ version: 1, devices: {} }),
        );
    } catch {
        console.warn(
            '[FileIntegrity] Unable to persist cache even after evicting all entries',
        );
    }
}

/**
 * Get hash entries for a specific device from the in-memory cache.
 * Returns an empty object if the device is not found.
 */
export function getHashCacheForDevice(
    deviceKey: string,
): Record<string, FileHashEntry> {
    ensureCacheLoaded();
    return inMemoryCache[deviceKey] ?? {};
}

/**
 * Update a single entry in the in-memory cache and persist to localStorage (debounced).
 * Creates the device record if it doesn't exist.
 * Persistence is debounced within 1 second to batch rapid mutations.
 */
export function updateHashEntry(
    deviceKey: string,
    path: string,
    hash: string,
): void {
    ensureCacheLoaded();

    if (!inMemoryCache[deviceKey]) {
        inMemoryCache[deviceKey] = {};
    }

    inMemoryCache[deviceKey][path] = {
        hash,
        timestamp: new Date().toISOString(),
        path,
    };

    debouncedPersistHashCache();
}

/**
 * Debounced persistence — batches rapid hash cache mutations into a single
 * localStorage write within PERSIST_DEBOUNCE_MS (1 second).
 */
function debouncedPersistHashCache(): void {
    if (persistDebounceTimer !== null) {
        clearTimeout(persistDebounceTimer);
    }

    persistDebounceTimer = setTimeout(() => {
        persistDebounceTimer = null;
        persistHashCache(inMemoryCache);
    }, PERSIST_DEBOUNCE_MS);
}

/**
 * Immediately flush any pending debounced persistence.
 * Useful for ensuring data is written before page unload or disconnection.
 */
export function flushHashCachePersistence(): void {
    if (persistDebounceTimer !== null) {
        clearTimeout(persistDebounceTimer);
        persistDebounceTimer = null;
        persistHashCache(inMemoryCache);
    }
}

// ─── Sync status determination ─────────────────────────────────────────────────

/**
 * Determine the sync status of a file based on stored and device hashes.
 *
 * - If no stored hash exists → "modified-on-device" (first time seeing this file)
 * - If hashes match → "in-sync"
 * - If hashes differ and file has local unsaved changes → "conflicted"
 * - If hashes differ and no local unsaved changes → "modified-on-device"
 */
export function determineSyncStatus(
    storedHash: string | undefined,
    deviceHash: string,
    isLocallyUnsaved: boolean,
): SyncStatus {
    if (!storedHash) return 'modified-on-device';
    if (storedHash === deviceHash) return 'in-sync';
    if (isLocallyUnsaved) return 'conflicted';
    return 'modified-on-device';
}

// ─── Testing helpers ───────────────────────────────────────────────────────────

/**
 * Reset the in-memory cache state. Used for testing only.
 * @internal
 */
export function _resetCacheForTesting(): void {
    inMemoryCache = {};
    cacheLoaded = false;
    if (persistDebounceTimer !== null) {
        clearTimeout(persistDebounceTimer);
        persistDebounceTimer = null;
    }
}

/**
 * Get the current in-memory cache. Used for testing only.
 * @internal
 */
export function _getInMemoryCache(): FileHashCache {
    return inMemoryCache;
}
